import { useEffect, useMemo, useState } from "react";
import {
    Calculator,
    Coins,
    CalendarRange,
    ArrowRight,
    Wallet,
    Banknote,
    Sparkles,
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { formatUSD } from "../lib/api";
import { CALCULADORA_CONFIG } from "../config/calculadora.config";
import { useCalculadora, buildPlanMessage } from "../context/CalculadoraContext";

const CFG = CALCULADORA_CONFIG;
const MIN_CUOTAS = CFG.diferido.minCuotas;
const MAX_CUOTAS = CFG.diferido.maxCuotas;

export default function CalculadoraWidget({ maestrias = [], variant = "hero" }) {
    // Determina modalidad inicial respetando config (si una está deshabilitada, usa la otra)
    const initialModalidad = CFG.modalidades[CFG.modalidadDefault]
        ? CFG.modalidadDefault
        : CFG.modalidades.diferido
        ? "diferido"
        : "contado";

    const [selectedPk, setSelectedPk] = useState("");
    const [modalidad, setModalidad] = useState(initialModalidad);
    const [cuotaInicial, setCuotaInicial] = useState(0);
    const [plazo, setPlazo] = useState(CFG.diferido.plazoDefault);
    const [descuentoContado, setDescuentoContado] = useState(CFG.contado.descuentoDefault);

    const selected = useMemo(
        () => maestrias.find((m) => String(m.pk) === String(selectedPk)),
        [maestrias, selectedPk]
    );

    const valorPrograma =
        selected?.cohortes_inscripcion_abierta?.[0]?.valor_programa || 0;

    useEffect(() => {
        if (!selectedPk && maestrias.length > 0) {
            setSelectedPk(String(maestrias[0].pk));
        }
    }, [maestrias, selectedPk]);

    useEffect(() => {
        // Sugerencia: % configurable de cuota inicial al cambiar de maestría
        if (valorPrograma) {
            setCuotaInicial(
                Math.round(valorPrograma * (CFG.diferido.porcentajeInicialSugerido / 100))
            );
        }
    }, [valorPrograma]);

    const saldo = Math.max(valorPrograma - cuotaInicial, 0);
    // Descuento promo plazo corto (ej: $200 si pago en 2-10 cuotas)
    const promoCfg = CFG.diferido.descuentoCuotasCortas || {};
    const aplicaDescuentoPlazoCorto =
        modalidad === "diferido" &&
        promoCfg.enabled &&
        plazo >= (promoCfg.plazoMin || 0) &&
        plazo <= (promoCfg.plazoMax || 0);
    const descuentoPlazoCorto = aplicaDescuentoPlazoCorto ? promoCfg.monto || 0 : 0;
    const saldoConDescuento = Math.max(saldo - descuentoPlazoCorto, 0);
    const cuotaMensual = plazo > 0 ? saldoConDescuento / plazo : 0;
    const totalAPagar = valorPrograma - descuentoPlazoCorto;
    const porcentajeInicial = valorPrograma
        ? Math.min(100, Math.round((cuotaInicial / valorPrograma) * 100))
        : 0;

    // Contado: Usa el estado interactivo configurado
    const descuentoActivo = modalidad === "contado" ? descuentoContado : 0;
    const ahorroContado = valorPrograma * (descuentoActivo / 100);
    const totalContado = valorPrograma - ahorroContado;

    // Sincroniza snapshot con el contexto compartido (lo lee el formulario)
    const { setSnapshot } = useCalculadora();
    useEffect(() => {
        setSnapshot({
            modalidad,
            nombreMaestria: selected?.nombre_maestria || "",
            cohorte: selected?.cohortes_inscripcion_abierta?.[0]?.cohorte || "",
            valorPrograma,
            cuotaInicial,
            plazo,
            saldo: saldoConDescuento,
            cuotaMensual,
            totalAPagar,
            descuentoPlazoCorto,
            descuentoPct: descuentoActivo,
            ahorroContado,
            totalContado,
        });
    }, [
        modalidad,
        selected,
        valorPrograma,
        cuotaInicial,
        plazo,
        saldoConDescuento,
        cuotaMensual,
        totalAPagar,
        descuentoPlazoCorto,
        descuentoActivo,
        ahorroContado,
        totalContado,
        setSnapshot,
    ]);

    // Si la calculadora está deshabilitada en config, no renderiza nada.
    if (!CFG.enabled) return null;

    const isHero = variant === "hero";

    return (
        <div
            data-testid="calculadora-widget"
            className={
                isHero
                    ? "liquid-glass rounded-3xl p-7 md:p-9 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] w-full max-w-xl text-white"
                    : "liquid-glass rounded-3xl p-7 md:p-9 shadow-xl w-full text-white"
            }
        >
            <div className="flex items-center gap-3 mb-7">
                <span className="grid place-items-center w-11 h-11 rounded-2xl bg-unemi-orange-primary/10 text-unemi-orange-primary">
                    <Calculator className="w-5 h-5" />
                </span>
                <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-unemi-orange-primary">
                        Calculadora
                    </p>
                    <h3 className="font-heading text-xl text-white">
                        Tu plan de financiamiento
                    </h3>
                </div>
            </div>

            <div className="space-y-5">
                <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">
                        Maestría
                    </Label>
                    <Select value={selectedPk} onValueChange={setSelectedPk}>
                        <SelectTrigger
                            data-testid="calc-maestria-select"
                            className="h-12 bg-white/5 border-white/10 rounded-xl text-white font-semibold"
                        >
                            <SelectValue placeholder="Selecciona una maestría" />
                        </SelectTrigger>
                        <SelectContent className="max-w-[--radix-select-trigger-width]">
                            {maestrias.map((m) => (
                                <SelectItem
                                    key={m.pk}
                                    value={String(m.pk)}
                                    data-testid={`calc-option-${m.pk}`}
                                >
                                    <span className="block truncate">
                                        {m.nombre_maestria}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                            Valor programa
                        </p>
                        <p
                            className="font-heading text-2xl font-semibold text-white mt-1 tabular-nums"
                        >
                            {formatUSD(valorPrograma)}
                        </p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                            Cohorte
                        </p>
                        <p className="font-semibold text-sm text-white mt-1.5 truncate">
                            {selected?.cohortes_inscripcion_abierta?.[0]?.cohorte || "—"}
                        </p>
                    </div>
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-white/60 mb-2 block">
                        {CFG.textos.labelFormaPago}
                    </Label>
                    <div className="grid grid-cols-2 gap-2 p-1 bg-white/5 rounded-xl">
                        {[
                            { id: "diferido", label: CFG.textos.labelDiferido, icon: Wallet },
                            { id: "contado", label: CFG.textos.labelContado, icon: Banknote },
                        ]
                            .filter((m) => CFG.modalidades[m.id])
                            .map((m) => {
                                const Icon = m.icon;
                                const active = modalidad === m.id;
                                return (
                                    <button
                                        key={m.id}
                                        type="button"
                                        data-testid={`calc-modalidad-${m.id}`}
                                        onClick={() => setModalidad(m.id)}
                                        className={
                                            "h-10 inline-flex items-center justify-center gap-2 rounded-lg text-sm font-bold transition-all " +
                                            (active
                                                ? "bg-unemi-orange-primary text-white shadow-sm"
                                                : "text-white/50 hover:text-white")
                                        }
                                    >
                                        <Icon className="w-4 h-4" />
                                        {m.label}
                                    </button>
                                );
                            })}
                    </div>
                </div>

                {modalidad === "diferido" && (
                    <>
                        <div>
                            <div className="flex justify-between items-baseline mb-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-white/60">
                                    <Coins className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                                    {CFG.textos.labelCuotaInicial}
                                </Label>
                                <span className="text-xs font-bold text-unemi-orange-primary tabular-nums">
                                    {porcentajeInicial}% del valor
                                </span>
                            </div>
                            <Input
                                data-testid="calc-cuota-inicial"
                                type="number"
                                min={0}
                                max={valorPrograma}
                                value={cuotaInicial}
                                onChange={(e) =>
                                    setCuotaInicial(
                                        Math.min(Number(e.target.value || 0), valorPrograma)
                                    )
                                }
                                className="h-12 bg-white/5 border-white/10 rounded-xl text-white font-semibold tabular-nums"
                            />
                            <Slider
                                data-testid="calc-cuota-inicial-slider"
                                min={0}
                                max={valorPrograma || 100}
                                step={50}
                                value={[cuotaInicial]}
                                onValueChange={(v) => setCuotaInicial(v[0])}
                                className="mt-3"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-baseline mb-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-white/60">
                                    <CalendarRange className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                                    Plazo
                                </Label>
                                <span className="text-xs font-bold text-unemi-orange-light tabular-nums">
                                    {plazo} {plazo === 1 ? "cuota" : "cuotas"}
                                </span>
                            </div>
                            <Slider
                                data-testid="calc-plazo-slider"
                                min={MIN_CUOTAS}
                                max={MAX_CUOTAS}
                                step={1}
                                value={[plazo]}
                                onValueChange={(v) => setPlazo(v[0])}
                                className="mt-1"
                            />
                            <div className="flex justify-between mt-2 text-[10px] uppercase tracking-wider font-bold text-white/50">
                                <span>{MIN_CUOTAS} cuotas</span>
                                <span>{MAX_CUOTAS} cuotas</span>
                            </div>
                        </div>
                    </>
                )}

                {/* Espacio reservado para descuento de contado si procede */}

                {modalidad === "diferido" ? (
                    <>
                    <div className="liquid-glass rounded-2xl border border-unemi-orange-primary/30 p-5">
                        {aplicaDescuentoPlazoCorto && (
                            <div className="mb-3 inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 font-bold px-3 py-1 rounded-full text-[10px] uppercase tracking-wider">
                                <Sparkles className="w-3 h-3" />
                                {promoCfg.etiqueta} · -{formatUSD(descuentoPlazoCorto)}
                            </div>
                        )}
                        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-unemi-orange-primary">
                            Cuota mensual estimada
                        </p>
                        <div className="flex items-baseline gap-2 flex-wrap mt-1">
                            <p
                                className="font-heading text-4xl md:text-5xl font-semibold text-white tabular-nums"
                            >
                                {formatUSD(cuotaMensual)}
                            </p>
                            <span className="font-heading text-lg md:text-xl text-slate-400 font-medium">
                                × {plazo} {plazo === 1 ? "mes" : "meses"}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-orange-200/60">
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                                    Saldo a financiar
                                </p>
                                <p
                                    className="font-semibold text-white tabular-nums"
                                >
                                    {formatUSD(saldoConDescuento)}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                                    Total a pagar
                                </p>
                                <p className="font-semibold text-white tabular-nums">
                                    {formatUSD(totalAPagar)}
                                </p>
                                {aplicaDescuentoPlazoCorto && (
                                    <p className="text-[10px] text-slate-400 line-through tabular-nums">
                                        {formatUSD(valorPrograma)}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Vista previa del mensaje formateado */}
                    <div data-testid="calc-preview-mensaje" className="liquid-glass rounded-2xl border border-white/10 p-5 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-unemi-orange-primary">
                                Vista previa del plan
                            </p>
                            {/* <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                                Lo que recibirá tu asesor
                            </span> */}
                        </div>
                        <pre className="font-body text-sm font-medium leading-relaxed text-white/90 whitespace-pre-wrap break-words bg-black/20 border border-white/5 rounded-xl p-4 max-h-72 overflow-y-auto">
{buildPlanMessage({
    modalidad,
    nombreMaestria: selected?.nombre_maestria || "",
    cohorte: selected?.cohortes_inscripcion_abierta?.[0]?.cohorte || "",
    valorPrograma,
    cuotaInicial,
    plazo,
    saldo: saldoConDescuento,
    cuotaMensual,
    totalAPagar,
    descuentoPlazoCorto,
    descuentoPct: descuentoActivo,
    ahorroContado,
    totalContado,
})}
                        </pre>
                    </div>
                    </>
                ) : (
                    <>


                    <div className="liquid-glass rounded-2xl border border-unemi-orange-primary/30 p-5">
                        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-unemi-orange-primary">
                            Pago único de contado
                        </p>
                        <p
                            data-testid="calc-total-contado"
                            className="font-heading text-4xl md:text-5xl font-semibold text-white mt-1 tabular-nums"
                        >
                            {formatUSD(totalContado)}
                        </p>
                        <p className="text-sm font-medium text-white/70 mt-2">
                            <span className="line-through text-slate-400">{formatUSD(valorPrograma)}</span>{" "}
                            <span className="text-unemi-orange-primary font-bold">con {descuentoActivo}% de descuento</span>
                        </p>
                        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-orange-200/60">
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                                    Ahorro
                                </p>
                                <p
                                    data-testid="calc-ahorro"
                                    className="font-semibold text-unemi-orange-primary tabular-nums"
                                >
                                    {formatUSD(ahorroContado)}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-white/60">
                                    Valor original
                                </p>
                                <p className="font-semibold text-white/80 tabular-nums line-through">
                                    {formatUSD(valorPrograma)}
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* Vista previa del mensaje (Contado) */}
                    <div className="liquid-glass rounded-2xl border border-white/10 p-5 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-unemi-orange-primary">
                                Vista previa del plan
                            </p>
                            {/* <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                                Lo que recibirá tu asesor
                            </span> */}
                        </div>
                        <pre className="font-body text-sm font-medium leading-relaxed text-white/90 whitespace-pre-wrap break-words bg-black/20 border border-white/5 rounded-xl p-4 max-h-72 overflow-y-auto">
{buildPlanMessage({
    modalidad,
    nombreMaestria: selected?.nombre_maestria || "",
    cohorte: selected?.cohortes_inscripcion_abierta?.[0]?.cohorte || "",
    valorPrograma,
    cuotaInicial,
    plazo,
    saldo: saldoConDescuento,
    cuotaMensual,
    totalAPagar,
    descuentoPlazoCorto,
    descuentoPct: descuentoActivo,
    ahorroContado,
    totalContado,
})}
                        </pre>
                    </div>
                    </>
                )}

                <button
                    type="button"
                    data-testid="calc-cta-inscribirse"
                    onClick={() => {
                        const el = document.getElementById("contacto");
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-unemi-orange-primary text-white font-bold hover:bg-unemi-orange-primary/90 shadow-lg shadow-unemi-orange-primary/30 transition-all"
                >
                    Quiero inscribirme
                    <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
